import React, { useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Badge from "aldoo-ra/Badge"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import DateFilterDropdown from "aldoo-ra/DateFilterDropdown"
import Tooltip from "aldoo-ra/Tooltip"
import { showNotification } from "aldoo-ra/Notification"
import UserPlatformAPI from "../user-api"
import UserInfoDrawer from "../UserInfoDrawer"
import { renderAvatar } from "../render-avatar"
import {
  UsersIcon,
  PlusCircleIcon,
  TrashIcon,
  EyeIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline"

// Import custom hooks
import { useUserSelection } from "./hooks/useUserSelection"
import { useUserFilters } from "./hooks/useUserFilters"
import { useUserModals } from "./hooks/useUserModals"

// Import utilities
import { buildRequestFields } from "./utils/filterHelpers"

// Import components
import UserStatistics from "./components/UserStatistics"
import UserCredentials from "./components/UserCredentials"
import UserDevices from "./components/UserDevices"

// Import modals
import CreateUserModal from "./components/modals/CreateUserModal"
import DeleteConfirmationModal from "./components/modals/DeleteConfirmationModal"
import UserCredentialsModal from "./components/modals/UserCredentialsModal"
import DevicesModal from "./components/modals/DevicesModal"
import RestoreUsersModal from "./components/modals/RestoreUsersModal"

/**
 * Main PlatformUsers component
 * Manages platform user accounts with CRUD operations, filtering, and statistics
 */
const PlatformUsers = () => {
  const [refreshKey, setRefreshKey] = useState(0)

  // Custom hooks for state management
  const {
    selectedUsers,
    selectedUserData,
    setSelectedUsers,
    setSelectedUserData,
    handleSelectUser,
    handleSelectAll,
    handleDeselectAll
  } = useUserSelection()

  const {
    statusFilter,
    credentialsFilter,
    dateFilter,
    handleStatusFilterChange,
    handleCredentialsFilterChange,
    handleDateFilterChange
  } = useUserFilters()

  const {
    userDetailsModal,
    devicesModal,
    credentialsModal,
    restoreModal,
    deleteConfirmModal,
    createUserModal,
    setUserDetailsModal,
    setRestoreModal,
    setDeleteConfirmModal,
    setCreateUserModal,
    handleViewUser,
    handleViewDevices,
    handleViewCredentials,
    closeUserDetailsModal,
    closeDevicesModal,
    closeCredentialsModal,
    closeDeleteConfirmModal
  } = useUserModals()

  // Build request fields based on filters
  const getRequestFields = () => {
    return buildRequestFields({
      statusFilter,
      credentialsFilter,
      dateFilter
    })
  }

  // Trigger refresh when filters change
  const handleFilterChange = (filterHandler) => (...args) => {
    filterHandler(...args)
    setRefreshKey(prev => prev + 1)
  }

  // Delete handlers
  const handleDeleteSelected = () => {
    if (selectedUsers.length === 0) return

    setDeleteConfirmModal({
      isOpen: true,
      userCount: selectedUsers.length,
      isBulk: true,
      onConfirm: async () => {
        try {
          const response = await UserPlatformAPI.DeleteUsers(selectedUsers)
          if (response.result) {
            setSelectedUsers([])
            setSelectedUserData([])
            setRefreshKey(prev => prev + 1)
          }
        } catch (error) {
          console.error("Failed to delete users:", error)
        }
        closeDeleteConfirmModal()
      }
    })
  }



  const handleRestoreUsers = async () => {
    // The restore dialog now handles individual restore actions
    // Just refresh the main table when restore dialog actions occur
    setRefreshKey(prev => prev + 1)
  }

  // Header controls for filters
  const headerControls = (
    <div className="w-full flex items-end gap-4">
      {/* Status Filter */}
      <div className="flex flex-col">
        <Typography
          variant="admin_txt2_dim"
          className="mb-0.5"
        >
          Status
        </Typography>

        <DropdownSelect
          variant="admin"
          options={[
            { value: "all", label: "All Status" },
            { value: "active", label: "Active" },
            { value: "disabled", label: "Disabled" }
          ]}
          value={statusFilter}
          onChange={handleFilterChange(handleStatusFilterChange)}
          fixedWidth="120px"
          showCheckbox={false}
        />
      </div>

      {/* Credentials Filter */}
      <div className="flex flex-col">
        <Typography
          variant="admin_txt2_dim"
          className="mb-0.5"
        >
          Credentials
        </Typography>

        <DropdownSelect
          variant="admin"
          options={[
            { value: "all", label: "All Credentials" },
            { value: "google", label: "Google" },
            { value: "apple", label: "Apple" },
            { value: "facebook", label: "Facebook" },
            { value: "internal", label: "Internal" },
            { value: "stripe", label: "Stripe" },
            { value: "none", label: "No Credentials" }
          ]}
          value={credentialsFilter}
          onChange={handleFilterChange(handleCredentialsFilterChange)}
          fixedWidth="150px"
          showCheckbox={false}
        />
      </div>

      {/* Date Filter */}
      <div className="flex flex-col">
        <DateFilterDropdown
          variant="admin"
          value={dateFilter}
          onChange={handleFilterChange(handleDateFilterChange)}
          className="w-[190px]"
          styleOverrides={{
            label: "text-admin_text_secondary text-xs font-normal mb-0.5",
          }}
        />
      </div>
    </div>
  )

  // Table column renders
  const tableRenders = [
    {
      id: "user",
      title: "User",
      headerClassName: "w-[30%]",
      render: (_, user) => (
        <button
          className="w-full flex items-center space-x-3 text-left"
          onClick={() => handleViewUser(user)}
        >
          {renderAvatar(user, "w-10 h-10")}

          <div className="w-0 flex-1">
            <Typography
              variant="admin_body"
              className="font-medium truncate my-0"
              data-user-name={user._id}
            >
              {user.name || "Unknown User"}
            </Typography>

            {user.email && (
              <Typography
                variant="admin_txt1_dim"
                className="block truncate my-0"
              >
                {user.email}
              </Typography>
            )}
          </div>
        </button>
      ),
    },
    {
      id: "mongoId",
      title: "ID",
      headerClassName: "w-[15%]",
      render: (_, user) => (
        <Tooltip
          display="block"
          content={
            <div className="text-center">
              <Typography
                variant="admin_txt1"
                className="block"
              >
                {user._id}
              </Typography>
              <Typography variant="admin_txt2_dim">
                Click to copy
              </Typography>
            </div>
          }
        >
          <button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(user._id)
                showNotification({
                  message: "MongoDB ID copied to clipboard",
                  className: "bg-admin_primary text-admin_text_inv"
                })
              } catch (error) {
                showNotification({
                  message: "Failed to copy MongoDB ID",
                  className: "bg-admin_error text-white"
                })
              }
            }}
            className="w-full text-left truncate hover:text-admin_primary transition-colors"
          >
            <Typography
              variant="admin_txt1"
              className="truncate text-admin_text_secondary hover:text-admin_primary"
            >
              {user._id}
            </Typography>
          </button>
        </Tooltip>
      ),
    },
    {
      id: "credentials",
      title: "Credentials",
      headerClassName: "w-[12%]",
      render: (_, user) => <UserCredentials user={user} onViewCredentials={handleViewCredentials} />,
    },
    {
      id: "devices",
      title: "Devices",
      headerClassName: "w-[10%]",
      render: (_, user) => <UserDevices user={user} onViewDevices={handleViewDevices} />,
    },
    {
      id: "status",
      title: "Status",
      headerClassName: "w-[10%]",
      render: (_, user) => (
        <Badge variant={user.enabled ? "admin_success" : "admin_error"}>
          {user.enabled ? "Active" : "Disabled"}
        </Badge>
      ),
    },
    {
      id: "created",
      title: "Created",
      headerClassName: "w-[10%]",
      render: (_, user) => (
        <Typography variant="admin_txt1" className="block text-admin_text_secondary">
          {new Date(user.created).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      id: "actions",
      title: "Actions",
      headerClassName: "w-[10%] text-center",
      render: (_, user) => (
        <div className="flex items-center justify-center">
          <button
            className="p-1.5 rounded hover:bg-admin_neutral_1/50 text-admin_text_secondary hover:text-admin_primary transition-colors"
            onClick={() => handleViewUser(user)}
            title="View user details"
          >
            <EyeIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="p-6">
      <UserStatistics onRefresh={refreshKey} />

      <div className="w-full flex items-center justify-between mb-5">
        <Typography
          component="h1"
          variant="admin_h3"
        >
          Platform Users
        </Typography>

        <div className="flex items-end justify-end gap-3">
          <Button
            variant="admin_secondary"
            onClick={() => setRestoreModal(true)}
          >
            <ArrowPathIcon className="w-4 h-4 mr-1" />
            Restore
          </Button>

          <Button
            variant="admin_primary"
            onClick={() => setCreateUserModal(true)}
          >
            <PlusCircleIcon className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
      </div>

      <DataTable
        localUserPreferencesKey="admin.platformAccess.userList"
        variant="admin_browser"
        apiMethod={UserPlatformAPI.GetUsers}
        requestFields={getRequestFields()}
        headerControls={headerControls}
        enableSelection={true}
        selectedItems={selectedUsers}
        selectedData={selectedUserData}
        onSelectionChange={(ids, data) => {
          setSelectedUsers(ids);
          setSelectedUserData(data);
        }}
        bulkActions={[
          {
            label: "Delete",
            variant: "admin_error",
            icon: <TrashIcon className="w-4 h-4" />,
            onClick: handleDeleteSelected,
          }
        ]}
        itemIdField="_id"
        searchField="name email google_id apple_id facebook_id internal_id stripe_customer_id _id"
        itemsPerPage={25}
        refreshKey={refreshKey}
        preferredHeight="70vh"
        styleOverrides={{
          table: "w-full table-fixed",
          header: "w-full flex justify-between items-end gap-2 -mt-5 mb-4",
          container: "relative w-full flex flex-col h-[72vh] justify-start bg-admin_paper rounded-3xl px-6 pt-8 pb-6",
          searchInput: "absolute w-[350px] h-[30px] top-6 right-4 text-text bg-transparent outline-none flex-1 px-2 py-1",
        }}
        renders={tableRenders}
        noData={
          <div className="text-center py-12">
            <UsersIcon className="w-16 h-16 text-admin_text_secondary mx-auto mb-4" />

            <Typography
              variant="admin_h4"
              className="text-admin_text_secondary mb-2"
            >
              No users found
            </Typography>

            <Typography variant="admin_txt1_dim">
              Start by adding your first platform user
            </Typography>
          </div>
        }
      />

      {/* Modals */}
      <UserInfoDrawer
        userObject={userDetailsModal.user}
        isOpen={userDetailsModal.isOpen}
        onClose={closeUserDetailsModal}
        onDelete={(user) => {
          setRefreshKey(prev => prev + 1)
          closeUserDetailsModal()
        }}
        onStatusChange={(user, enabled) => {
          setRefreshKey(prev => prev + 1)
        }}
      />

      <DevicesModal
        user={devicesModal.user}
        isOpen={devicesModal.isOpen}
        onClose={closeDevicesModal}
      />

      <UserCredentialsModal
        user={credentialsModal.user}
        isOpen={credentialsModal.isOpen}
        onClose={closeCredentialsModal}
      />

      <RestoreUsersModal
        isOpen={restoreModal}
        onClose={() => setRestoreModal(false)}
        onRestore={handleRestoreUsers}
      />

      <DeleteConfirmationModal
        isOpen={deleteConfirmModal.isOpen}
        onClose={closeDeleteConfirmModal}
        onConfirm={deleteConfirmModal.onConfirm}
        userCount={deleteConfirmModal.userCount}
        isBulk={deleteConfirmModal.isBulk}
      />

      <CreateUserModal
        isOpen={createUserModal}
        onClose={() => setCreateUserModal(false)}
        onSuccess={() => {
          // Refresh the data table when a new user is created
          setRefreshKey(prev => prev + 1)
        }}
      />
    </div>
  )
}

export default PlatformUsers
